import React, { useMemo } from 'react'
import useDispatch from '~editor/hooks/useDispatch'
import {
  AnyActionType,
  ActionTypes,
  getActionTypeLabel,
  IActionComponentProps,
  MouseMoveAction,
} from '~core/forge.type'
import { Text, Box, Flex, Tooltip } from '@chakra-ui/react'
import { Select } from '~editor/components/Select'
import StyledNumberInput from '~editor/components/StyledNumberInput'
import { IconElement } from '~core/components/icon/icon-element'
import { COLORS } from '~core/components/starCitizen/aegis/constants'
import { SHARD_SLOTS_WARNING } from '~editor/utils/constants'

interface IProps extends IActionComponentProps {
  action: MouseMoveAction
}

const ActionTypesOptions = MouseMoveAction.getAllowedTypes().map(
  (actionType) => ({
    label: getActionTypeLabel(actionType),
    value: actionType,
  }),
)

export const MouseMoveActionComponent = ({
  action,
  actions,
  activeActionGesture,
  isDisabled,
}: IProps) => {
  const dispatch = useDispatch()
  const selectedActionTypeOption = useMemo(
    () => ActionTypesOptions.find((option) => option.value === action.type),
    [action, action.type],
  )

  const isAbsolute =
    selectedActionTypeOption?.value === ActionTypes.mouseMoveAbsolute

  return (
    <Flex flex="1" alignItems="center" gap="12px">
      <Tooltip
        variant="warning"
        label="Use as your own risk - it may trigger anti-cheat in some games. GameGlass is not responsible for any consequences in this action's usage."
      >
        <Flex flex="1" alignItems="center" gap="5px" color={COLORS.warning}>
          <Box paddingTop="2px">WARNING</Box>
          <IconElement
            name="fas.faTriangleExclamation"
            style={{ width: '14px', height: '14px' }}
          />
        </Flex>
      </Tooltip>
      <Flex flex="1" alignItems="center">
        <Text color="ui.text.grey" fontWeight="700" marginRight="5px">
          {'X:'}
        </Text>
        <StyledNumberInput
          step={1}
          min={isAbsolute ? 0 : -99999999}
          max={99999999}
          precision={0}
          NumberInputFieldProps={{
            width: '50px',
          }}
          width="50px"
          keepWithinRange={true}
          marginRight="5px"
          value={action.x}
          onChange={(stringValue) => {
            let value = Math.round(parseInt(stringValue || '0'))
            if (isAbsolute) {
              value = Math.abs(value)
            }
            dispatch.nodes.updateActions({
              actionGestureType: activeActionGesture,
              actions: actions.map((_a: AnyActionType) => {
                const _action = { ..._a } as MouseMoveAction

                if (action.id === _action.id) {
                  _action.x = value
                }

                return _action
              }),
            })
          }}
        />
        <Text color="ui.text.grey" fontWeight="700" marginRight="5px">
          {'Y:'}
        </Text>
        <StyledNumberInput
          step={1}
          min={isAbsolute ? 0 : -99999999}
          max={99999999}
          precision={0}
          NumberInputFieldProps={{
            width: '50px',
          }}
          width="50px"
          keepWithinRange={true}
          marginRight="5px"
          value={action.y}
          onChange={(stringValue) => {
            let value = Math.round(parseInt(stringValue || '0'))
            if (isAbsolute) {
              value = Math.abs(value)
            }
            dispatch.nodes.updateActions({
              actionGestureType: activeActionGesture,
              actions: actions.map((_a: AnyActionType) => {
                const _action = { ..._a } as MouseMoveAction

                if (action.id === _action.id) {
                  _action.y = value
                }

                return _action
              }),
            })
          }}
        />
      </Flex>
      <Text color="ui.text.grey" fontWeight="700">
        {'|'}
      </Text>
      <Select
        width="130px"
        height="32px"
        options={ActionTypesOptions}
        defaultValue={ActionTypesOptions[0]}
        isSearchable={false}
        isDisabled={isDisabled}
        value={selectedActionTypeOption}
        onChange={(option) => {
          if (!option) return

          const value: ActionTypes = (option as any).value

          dispatch.nodes.updateActions({
            actionGestureType: activeActionGesture,
            actions: actions.map((_a: AnyActionType) => {
              const _action = { ..._a }

              if (action.id === _action.id) {
                _action.type = value
              }

              return _action
            }),
          })
        }}
      />
    </Flex>
  )
}

export default MouseMoveActionComponent
